import {
  Autocomplete,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Grid } from "@mui/system";
import { useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";

export interface ConfigDraft {
  name: string;
  report: string;
  cm: string;
  sfm: string;
  ft: string;
}

interface Props {
  draft: ConfigDraft;
  onChange: (draft: ConfigDraft) => void;
  /** false on an existing config - the report is fixed once it exists */
  canPickReport: boolean;
  readOnly?: boolean;
  onCreateReport?: () => void;
}

/**
 * Identity + recipe, the whole of a config. A config contributes no objects -
 * only the recipe - so this is the entire form on both create and edit.
 */
const ConfigDefinitionFields = ({
  draft,
  onChange,
  canPickReport,
  readOnly = false,
  onCreateReport,
}: Props) => {
  const {
    reports,
    interfaces,
    collectionMethods,
    subFilteringMethods,
    formatTypes,
  } = useSelector((state: AppState) => state.fetcherSettings);

  const interfaceName = (interfaceId: string) =>
    interfaces.find((row) => row.id === interfaceId)?.name ?? "?";

  const reportOptions = [...reports]
    .sort(
      (a, b) =>
        interfaceName(a.iface).localeCompare(interfaceName(b.iface)) ||
        a.name.localeCompare(b.name),
    )
    .map((report) => ({
      value: report.id,
      label: `${interfaceName(report.iface)} - ${report.name} (${report.id})`,
    }));

  const selectedReport = reports.find((row) => row.id === draft.report);

  return (
    <Stack spacing={2.5}>
      <Typography
        sx={{
          fontSize: "11px",
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: "#00000099",
          fontWeight: 700,
        }}
      >
        Identity
      </Typography>

      <TextField
        required
        fullWidth
        size="small"
        label="Configuration Name"
        placeholder="e.g. IA_earning_before_month"
        helperText="Max 100 chars."
        inputProps={{ maxLength: 100 }}
        value={draft.name}
        disabled={readOnly}
        onChange={(event) => onChange({ ...draft, name: event.target.value })}
        color="secondary"
      />

      <Typography
        sx={{
          fontSize: "11px",
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: "#00000099",
          fontWeight: 700,
        }}
      >
        Recipe
      </Typography>

      <Stack direction="row" spacing={1} alignItems="flex-start">
        {canPickReport ? (
          <Autocomplete
            sx={{ flex: 1 }}
            size="small"
            options={reportOptions}
            value={
              reportOptions.find((option) => option.value === draft.report) ??
              null
            }
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            onChange={(_, value) =>
              onChange({ ...draft, report: value?.value ?? "" })
            }
            disabled={readOnly}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Report"
                color="secondary"
              />
            )}
          />
        ) : (
          <TextField
            sx={{ flex: 1 }}
            size="small"
            label="Report"
            value={
              selectedReport
                ? `${selectedReport.name} (${selectedReport.id})`
                : "-"
            }
            disabled
            helperText="The report is fixed once a config exists — changing it would repoint every connected brand."
            color="secondary"
          />
        )}
        {!readOnly && onCreateReport && (
          <Button color="secondary" onClick={onCreateReport} sx={{ mt: 0.25 }}>
            Create new report
          </Button>
        )}
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            select
            required
            fullWidth
            size="small"
            label="Collection Method"
            value={draft.cm}
            disabled={readOnly}
            onChange={(event) => onChange({ ...draft, cm: event.target.value })}
            color="secondary"
          >
            {collectionMethods.map((method) => (
              <MenuItem key={method.id} value={method.id}>
                {method.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            select
            required
            fullWidth
            size="small"
            label="Sub-Filtering Method"
            value={draft.sfm}
            disabled={readOnly}
            onChange={(event) =>
              onChange({ ...draft, sfm: event.target.value })
            }
            color="secondary"
          >
            {subFilteringMethods.map((method) => (
              <MenuItem key={method.id} value={method.id}>
                {method.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            select
            required
            fullWidth
            size="small"
            label="Format Type"
            value={draft.ft}
            disabled={readOnly}
            onChange={(event) => onChange({ ...draft, ft: event.target.value })}
            color="secondary"
          >
            {formatTypes.map((type) => (
              <MenuItem
                key={type.id}
                value={type.id}
                disabled={type.used === 0}
              >
                {type.name}
                {type.used === 0 ? " - not used" : ""}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Collection Type"
            value={selectedReport?.ctype ?? ""}
            disabled
            helperText="Inherited from the report."
            color="secondary"
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ConfigDefinitionFields;
